import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { autoRedirect } from '../../api/auth';
import { autoRedirectUser } from '../../helpers/bizDataHelpers';
import LocalStorageHelper from '../../helpers/localStorageHelpers';
import {
  redirectHostForApp,
  shouldSelectAtlasEnv,
} from '../../helpers/environment';
import SelectAtlasEnv from '../../components/SelectAtlasEnv';
import Spin from 'antd/lib/spin';

function AppRedirect() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const appToRedirect = searchParams.get('app_to_redirect');
  const redirectResponseRef = useRef(null);
  const minTimePassedRef = useRef(false);
  // Redirect params held back while the user picks an Atlas environment
  // (test builds only, see shouldSelectAtlasEnv).
  const [pendingParams, setPendingParams] = useState(null);

  // Only redirect once BOTH the API has responded AND the minimum time has elapsed
  const handleRedirectWhenReady = () => {
    if (redirectResponseRef.current !== null && minTimePassedRef.current) {
      autoRedirectUser(redirectResponseRef.current, appToRedirect);
    }
  };

  const { mutate: callAutoRedirect } = useMutation({
    mutationFn: autoRedirect,
    onSuccess: response => {
      redirectResponseRef.current = response;
      handleRedirectWhenReady();
    },
    onError: () => navigate('/business'),
  });

  useEffect(() => {
    if (!LocalStorageHelper.getAccessToken()) {
      navigate('/login/email-mobile');
      return;
    }

    const params = {
      app_name: searchParams.get('app_name'),
      biz_id: searchParams.get('biz_id'),
      user_id: searchParams.get('user_id'),
      next: appToRedirect,
    };

    const redirectHost = redirectHostForApp(
      params.app_name,
      LocalStorageHelper.getRedirectHost(),
    );

    if (shouldSelectAtlasEnv(params.app_name, redirectHost)) {
      setPendingParams(params);
    } else if (redirectHost) {
      callAutoRedirect({ ...params, redirect_host: redirectHost });
    } else {
      callAutoRedirect(params);
    }

    const timer = setTimeout(() => {
      minTimePassedRef.current = true;
      handleRedirectWhenReady();
    }, 1000);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function onSelectEnv(env) {
    callAutoRedirect({ ...pendingParams, redirect_host: env.host });
    setPendingParams(null);
  }

  if (pendingParams) {
    // Closing abandons the auto redirect; fall back to business selection
    // like the redirect API error path does.
    return (
      <SelectAtlasEnv onSelect={onSelectEnv} onClose={() => navigate('/business')} />
    );
  }

  return  <Spin size="large" />
}

export default AppRedirect;
